import { defineRule } from 'vee-validate';
import EventEmitter from 'events';
import TypedEventEmitter from 'typed-emitter';
import { SdkFlowpageApi } from '@/sdk/api/flowPages';
import { SdkEvents } from './interface';
import { SdkCampaignApi } from '@/sdk/api/campaign';
import { SdkPopoverApi } from './api/popover';
import { SdkReplacementTagsApi } from './api/replacementTags';
import { isSsr } from '@/utilities/Utilities';

// Extend the global Window object to include our own sdk.
declare global {
  interface Window {
    sdk?: Sdk;
  }
}
export const sdkEvents = new EventEmitter() as TypedEventEmitter<SdkEvents>;

/**
 * SDK interface class. This is a singleton class.
 */
class Sdk {
  version = '0.0.1';
  // Add event listener.
  on<E extends keyof SdkEvents>(event: E, listener: SdkEvents[E]): this {
    if (isSsr()) {
      // eslint-disable-next-line no-console
      console.info(
        '%c\nYou are listening to the Leadfamly SDK. \nFind the documentation here: https://leadfamly.github.io \n',
        'color: #242424; background: #42b883; font-size: 16px; line-height: 1.6; padding: 0 8px'
      );
    }
    sdkEvents.on(event, listener);
    return this;
  }

  // Add event listener & auto-remove after first emit.
  once<E extends keyof SdkEvents>(event: E, listener: SdkEvents[E]): this {
    sdkEvents.once(event, listener);
    return this;
  }

  // De-register an event listener.
  off<E extends keyof SdkEvents>(event: E, listener: SdkEvents[E]): this {
    sdkEvents.off(event, listener);
    return this;
  }

  defineValidationRule(name: string, fn: (value: string) => boolean) {
    defineRule(name, fn);
  }

  flowPages = SdkFlowpageApi;
  campaign = SdkCampaignApi;
  popover = SdkPopoverApi;
  replacementTags = SdkReplacementTagsApi;
}

const initializeSDK = () => {
  // Make SDK available in window object.
  if (typeof window !== 'undefined') {
    window.sdk = new Sdk();

    window.dispatchEvent(new Event('lf-sdk-ready', { bubbles: true, cancelable: false, composed: true }));
  }
};

export default initializeSDK;
